import {
  decorate,
  observable,
  reaction,
  action,
  computed,
  configure,
  toJS,
} from 'mobx';

import {
  API_AUCTIONS_GET_ENDPOINT,
  API_LANES_GET_ENDPOINT,
} from '../shared/constants';

import { getListDataById, loadData } from './utils';

import {getStoredValue, setStoredAuctionIdByPath} from '../shared/utils/localStorage';

import { getTestAuctionList, getTestLaneList } from './testStore';
import { orderBy, filter } from 'lodash';

configure({ enforceActions: 'always' });

class AuctionStore {
  constructor() {
    this.initialize();
  }

  setProperty = (key, value) => {
    this[key] = value;
  };

  getProperty = (key) => {
    return this[key];
  };

  initialize = () => {
    this.initializeAuction();
  };

  initializeAuction = () => {
    this.setProperty('auctionList', []);
    this.setProperty('auctionID', null);
    // this.setProperty('auctionID', getStoredAuctionIdByPath());
    this.setProperty('laneList', []);
    this.setProperty('laneStatus', null);
    this.setProperty('auctionName', null);
    this.setProperty('totalItems', 0);
    this.setProperty('isLoading', true);
    this.setProperty('data', []);
    this.setProperty('auctions', []);
    this.setProperty('lanes', []);
    this.setProperty('laneOptions', []);
    this.setProperty('totalUploadedItems', 0);
    this.setProperty('connecting', false);
    this.setProperty('auctionSettings', []);
    this.setProperty('auctionInfo', {});
    this.setProperty('sortedActiveAuctions', []);
    this.initializeLane();
  };

  initializeLane = () => {
    this.setProperty('laneList', []);
    this.setProperty('laneMetrics', null);
    this.setProperty('auctionLaneName', null);
    this.setProperty('auctionLaneId', null);
    this.setProperty('lastAuctionLaneId', null);
    this.initializeProducts();
  };

  initializeProducts = () => {
    this.setProperty('runList', []);
    this.setProperty('runListStats', null);
    this.setProperty('runListFiltered', []);
  };

  processData = (key, { data, totalItems }) => {
    this.setProperty(key, toJS(data));
    this.setProperty('totalItems', totalItems || 0);
    this.setProperty('isLoading', false);
    // console.log('loadAuctions > processData', { key, data, totalItems });
    return { key, data: toJS(data), totalItems };
  };

  loadAuctionLaneOptions = async (auctionId) =>
    await this.loadAuctionLanes(auctionId)
      .then((res) => res.data)
      .then((lanes) => {
        const laneOptions = lanes.map(({ laneID, lane }) => ({
          key: laneID,
          value: laneID,
          text: lane,
        }));
        const firstOption = {
          key: 'ALL',
          value: 'ALL',
          text: `All Lanes (${laneOptions.length})`,
        };
        const allOptions = [firstOption, ...laneOptions];
        this.setProperty('laneOptions', allOptions);
        return allOptions;
      })
      .catch(console.error);

  loadAuctionLanes = async (auctionID) => {
    const lanesUrl = API_LANES_GET_ENDPOINT;
    const params = { auctionID };

    this.isLoading = true;
    const res = await loadData(params, lanesUrl);

    const {
      data: { lanes, totalItems },
    } = res;

    return this.processData('lanes', { data: lanes, totalItems });
  };

  getSortedActiveAuctions = async () => {
    this.loadAuctions(null, 1, 1000, null, null, null)
      .then((res) => res.data)
      .then((data) => {
        const auctionData = filter(
          data,
          (auction) =>
            auction.auctionState !== 'Superseded' &&
            auction.auctionState !== 'Test',
        );

        const orderedData = orderBy(auctionData, ['startTime'], ['desc']).map(
          ({ auctionID, auction }) => ({
            key: auctionID,
            value: auctionID,
            text: auction,
          }),
        );
        // console.log('getSortedActiveAuctions', JSON.stringify(orderedData));
        this.setProperty('sortedActiveAuctions', toJS(orderedData));
        return toJS(orderedData);
      });
  };

  loadAuctions = async (
    searchText,
    pageNumber,
    pageSize,
    sortColumn,
    sortDirection,
    auctionState,
  ) => {

    const sessionToken = sessionStorage.getItem("simulcastadmin_session");

    const params = {
      name: searchText && searchText[0]?.searchValue,
      pageNumber,
      pageSize,
      sortBy: sortColumn,
      sortDirection,
      auctionState,
      sessionToken
    };

    this.isLoading = true;
    const res = await loadData(params, API_AUCTIONS_GET_ENDPOINT);

    const {
      data: {
        pagedAuctions: { items, totalItems },
      },
    } = res;
    // console.log('loadAuctions', JSON.stringify(res));
    return this.processData('auctions', { data: items, totalItems });
  };

  getLanesByAuctionId = (
    auctionID,
    searchText,
    pageNumber,
    pageSize,
    sortColumn,
    sortDirection,
    // auctionName,
  ) => {
    if (!auctionID) {
      return;
    }

    const params = {
      auctionID,
      name: searchText[0]?.searchValue,
      sortBy: sortColumn,
      sortDirection,
    };

    this.isLoading = true;
    loadData(params, API_LANES_GET_ENDPOINT).then((res) => {
      const {
        data: { lanes },
      } = res;
      this.processData('lanes', { data: lanes, totalItems: lanes?.length });
    });
  };

  setTotalItemLoaded = (data) => {
    var sumItems = 0;
    var sumUploaded = 0;
    for (let i = 0; i < data.length; i++) {
      sumUploaded += data[i].totalMediaUploaded;
      sumItems += data[i].totalMedia;
    }
    this.setProperty('totalItems', sumItems);
    this.setProperty('totalUploadedItems', sumUploaded);
  };

  setAuctionList = (data) => {
    //data = getTestAuctionList(); // FG eventually should call the api
    const noAuctionData = !this.auctionList.length;
    if (noAuctionData) this.auctionList = data;
    this.setProperty('auctionList', this.auctionList); //FG - set mock data
  };

  setLaneList = (data) => {
    data = getTestLaneList(); // FG eventually should call the api
    const noLaneData = !this.laneList.length;
    if (noLaneData) this.laneList = data;
    this.setProperty('laneList', this.laneList); //FG - set mock data
  };
}

decorate(AuctionStore, {
  auctions: observable,
  lanes: observable,
  getLanesByAuctionId: action,
  auctionID: observable,
  auctionInfo: observable,
  auctionLaneId: observable,
  auctionLaneName: observable,
  auctionList: observable,
  auctionName: observable,
  totalUploadedItems: observable,
  totalItems: observable,
  laneOptions: observable,
  connected: observable,
  // getAuctionAndLaneTitle: computed,
  // getAuctionLaneIdForInvocation: computed,
  // getListDataById: action,
  // getProperty: action,
  initialize: action,
  initializeAuction: action,
  initializeLane: action,
  initializeProducts: action,
  laneList: observable,
  laneMetrics: observable,
  laneStatus: observable,
  lastAuctionLaneId: observable,
  runList: observable,
  runListFiltered: observable,
  runListStats: observable,
  setAuction: action,
  setAuctionList: action,
  setAuctionSettings: action,
  setFilteredRunList: action,
  setLane: action,
  setLaneList: action,
  setTotalItemLoaded: action,
  setLaneMetrics: action,
  setLayoutSettings: action,
  setProperty: action,
  setRunList: action,
  settings: observable,
  toggleLaneMedia: action,
  updateRunListItemState: action,
  updateRunListWithPropValue: action,
  isLoading: observable,
  data: observable,
  processData: action,
  loadAuctions: action,
  getSortedActiveAuctionOptions: action,
  getSortedActiveAuctions: action,
  sortedActiveAuctions: observable,
  loadAuctionLanes: action,
  getAuctionLaneOptions: action,
});

export default AuctionStore;
